import { array, option, ord, string } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import { Option } from '@mews-commander/types';
import { createOption } from '@mews-commander/ui';
import { useLocalizeText, useLocalizeTextOrNull } from '@mews/framework';
import { upperFirst } from '@mews/std';
import { SelectField, SelectFieldProps } from '../SelectField';

interface UseFlagOptionsConfig<F> {
    enabledValues?: Partial<Record<keyof F, boolean>>;
    excludedValues?: Partial<Record<keyof F, boolean>>;
    disabledValues?: Partial<Record<keyof F, boolean>>;
    overrideFlagTextKeys?: Record<keyof F, Mews.TextKeyUnion>;
    omitHint?: boolean;
}

export const orderByLabel = pipe(
    string.Ord,
    ord.contramap(({ label }: Pick<Option, 'label'>) => (typeof label === 'string' ? label.toLocaleLowerCase() : ''))
);

const useFlagOptions = <F, N extends string = string>(
    flagValues: Array<Extract<keyof F, string>>,
    flagName: N,
    { enabledValues, excludedValues, disabledValues, overrideFlagTextKeys, omitHint }: UseFlagOptionsConfig<F>
): Array<Option<Extract<keyof F, string>>> => {
    const localizeText = useLocalizeText();
    const localizeTextOrNull = useLocalizeTextOrNull();
    return pipe(
        flagValues,
        (values) => (enabledValues ? values.filter((value) => enabledValues[value]) : values),
        (values) => (excludedValues ? values.filter((value) => !excludedValues[value]) : values),
        array.map((flagValue) => {
            const label = localizeText({
                textKey: (overrideFlagTextKeys?.[flagValue] ?? `${flagName}${upperFirst(flagValue)}`) as Mews.TextKey,
            });
            return createOption<Extract<keyof F, string>>({
                value: flagValue,
                label,
                secondaryText: omitHint
                    ? null
                    : localizeTextOrNull({
                          textKey: (overrideFlagTextKeys?.[flagValue]
                              ? `${overrideFlagTextKeys[flagValue]}Hint`
                              : `${flagName}${upperFirst(flagValue)}Hint`) as Mews.TextKey,
                      }),
                disabled: disabledValues?.[flagValue],
            });
        }),
        array.sort(orderByLabel)
    );
};

export interface FlagSelectFieldProps<F, N extends string = string>
    extends Omit<SelectFieldProps, 'options'>,
        UseFlagOptionsConfig<F> {
    flagName: N;
    flagValues: Array<Extract<keyof F, string>>;
}

/**
 * A select field component that allows for the selection of multiple flag values.
 * - The flags are localized by creating text keys that are composed using the flag name and flag value e.g. "ProductOptions" + "BillAsPackage" = "ProductOptionsBillAsPackage".
 * - The associated hint is localized by appending "Hint" to the text key e.g. "ProductOptionsBillAsPackageHint".
 * - The values are internally stored as Record<keyof F, boolean>.
 *
 * @template F - The type representing the flag interface, where keys are flag names and values are booleans.
 * @template N - The name of the flag used to prefix text keys for localization. Needs to be explicitly passed for overrideFlagTextKeys to suggest correctly prefixed text keys.
 *
 * @param props.flagName - The name of the flag, used as a prefix for text keys.
 * @param props.flagValues - An array of flag values to be used as options. By explicitly typing F, the keys are ensured to be valid flag values.
 * @param props.enabledValues - A record of flag values that should be enabled.
 * @param props.excludedValues - A record of flag values that should be excluded.
 * @param props.disabledValues - A record of flag values that should be disabled i.e., greyed out.
 * @param props.overrideFlagTextKeys - A record of custom text keys for flag labels when the text key is not the composed of the flag name and flag value.
 * @param props.omitHint - A boolean indicating whether to omit hints for the flags.
 * @param props - Additional properties for the SelectField component.
 *
 * @returns The rendered FlagSelectField component.
 */
export const FlagSelectField = <F, N extends string = string>({
    flagName,
    flagValues,
    enabledValues,
    excludedValues,
    disabledValues,
    overrideFlagTextKeys,
    omitHint,
    ...props
}: FlagSelectFieldProps<F, N>) => {
    type Flags = Record<keyof F, boolean>;

    return (
        <SelectField
            {...props}
            options={useFlagOptions(flagValues, flagName, {
                enabledValues,
                excludedValues,
                disabledValues,
                overrideFlagTextKeys,
                omitHint,
            })}
            transform={{
                input: (input) =>
                    flagValues.reduce((flags, flagValue) => {
                        flags[flagValue] = (input as Array<keyof Flags>).includes(flagValue);
                        return flags;
                    }, {} as Flags),
                output: (output) =>
                    output
                        ? pipe(
                              output as Flags,
                              Object.entries<boolean>,
                              array.filterMap(([key, value]) => (value ? option.some(key) : option.none))
                          )
                        : [],
            }}
            multiselect
        />
    );
};
